const workflow = {
  stagedMode: 'Proceed by stage',
  quickMode: 'Quick run',
  startTitle: 'Phase-based workflow',
  startDescription: 'Choose the phase, input pattern and granularity, then work through the process with intermediate results saved as you go.',
      cards: {
        accept: 'Accept',
        acceptanceCriteria: 'Acceptance Criteria',
        adoptContent: 'Adopt content',
        aiCandidate: 'AI candidate',
        aiExpectedEffect: 'AI expected effect {{score}}',
        aiSeverityCandidate: 'AI severity candidate {{score}}',
        cause: 'Cause',
        changeDescription: 'Change Description',
        changeItem: 'Change {{number}}',
        confidence: 'Confidence {{percent}}%',
        confirmSod: 'Confirm S/O/D',
        confirmedEffect: 'Validated effectiveness',
        countermeasure: 'Countermeasure',
        countermeasureRequirement: 'Countermeasure required',
        defer: 'Defer',
        effect: 'Effect',
        failureMode: 'Failure Mode',
        feasibility: 'Feasibility',
        mechanism: 'Mechanism',
        needsInfo: 'Needs review',
        newSpecification: 'New Specification',
        notRequired: 'Not required',
        notRequiredReason: 'Reason no countermeasure is required',
        oldSpecification: 'Old Specification',
        reject: 'Reject',
        required: 'Required',
        requiredForSod: 'Required to confirm S/O/D',
        scoringStatus: 'Scoring: {{status}}',
        targetCause: 'Target Cause',
        unconfirmed: 'Unconfirmed: {{items}}',
        verificationMethod: 'Verification Method',
      },
      input: {
        changeGranularityFieldLabel: 'Change Granularity',
        changeReportLabel: 'Change-Point Material',
        description: 'Register the old/new information and change-point materials used in this process.',
        extractChanges: 'Extract Changes',
        granularityCoarse: 'Coarse Granularity',
        granularityFine: 'Fine Granularity',
        granularityMixed: 'Mixed',
        inputPatternChangeReport: 'Change-Point Material',
        inputPatternFieldLabel: 'Input Pattern',
        inputPatternFunctionPartMatrix: 'Function List × Part Tree',
        inputPatternOldNewCad: 'Old/New CAD',
        inputPatternOldNewDocuments: 'Old/New Documents',
        inputPatternOldNewDrawings: 'Old/New Drawings',
        newCadModelIdLabel: 'New CAD Model ID',
        newDrawingExtractedText: 'Extracted Text (New Drawing)',
        newSpecLabel: 'New Specification',
        oldCadModelIdLabel: 'Old CAD Model ID',
        oldDrawingExtractedText: 'Extracted Text (Old Drawing)',
        oldSpecLabel: 'Old Specification',
        phaseConceptEstimate: 'Concept & Estimate',
        phaseDetailedDesign: 'Detailed Design',
        phaseFieldLabel: 'Process Phase',
        phaseFmeaDrbfm: 'FMEA/DRBFM',
        phasePostProcessCheck: 'Post-Process Check',
        phaseRequirementsChange: 'Requirements Change',
        startProcess: 'Start Process',
        title: 'Input Artifacts',
      },
      logic: {
        cadIdsRequired: 'Enter the old CAD ID and the new CAD ID',
        changeReportRequired: 'Enter the change-point document',
        changeReportTitle: 'Change-Point Document',
        drawingsTextRequired: 'Enter the extracted text for the old and new drawings',
        extractChangesFailed: 'Failed to extract design changes',
        generateCountermeasuresFailed: 'Failed to generate countermeasures',
        generateFailureModesFailed: 'Failed to generate failure modes',
        newCadTitle: 'New CAD',
        newDrawingTitle: 'New Drawing',
        newSpecTitle: 'New Specification',
        normalizeChangesFailed: 'Failed to normalize design changes',
        oldCadTitle: 'Old CAD',
        oldDrawingTitle: 'Old Drawing',
        oldSpecTitle: 'Old Specification',
        sessionTitle: '{{itemName}} Staged DRBFM',
        specsRequired: 'Enter the old and new specifications',
        stageRunFailed: 'Failed to run the DRBFM stage',
      },
      matrix: {
        affectedRequirements: 'Affected function requirements',
        aiCandidate: ' (suggested: {{status}})',
        aiDraft: 'AI draft',
        cellJudgment: 'Cell assessment',
        changeSummary: 'Change point summary',
        changedCount: 'Changed {{count}}',
        excelImport: 'Import Excel',
        functionHierarchyHeader: 'Function hierarchy',
        gridAriaLabel: 'Change point extraction matrix',
        importHint: 'Import an .xlsx that contains "Function List", the part hierarchy ("Part Tree" or "Hierarchy Diagram"), and change points ("Change Point Extraction Matrix" or "Change/Variation Points").',
        importantChangedCount: 'Important change {{count}}',
        importantNeedsRequirement: 'For an important change, select at least one affected function requirement.',
        importantWithoutRequirementsCount: 'Requirement unspecified {{count}}',
        inspectionAxis: 'Inspection axis',
        inspectionAxisHintHeader: 'Inspection axis hints',
        judgment: 'Assessment',
        newValue: 'After',
        oldValue: 'Before',
        part: 'Part',
        partNameHeader: 'Part name',
        partNoHeader: 'Part No.',
        partTreeAriaLabel: 'Part tree',
        partTreeCount: 'Part tree ({{count}})',
        promotedCannotEdit: 'This cell has been promoted to a design change and can no longer be edited.',
        promotionRequirements: 'Promotion requires every cell to be reviewed and at least one changed cell. Important changes must also specify their affected requirements.',
        requirementHeader: 'Requirement',
        requirementListAriaLabel: 'Function requirements',
        requirementsCount: 'Function requirements ({{count}})',
        reviewedCount: 'Reviewed {{reviewed}}/{{total}}',
        reviewerNote: 'Review note',
        roleHeader: 'Role',
        saveJudgment: 'Save assessment',
        shortChanged: 'Changed',
        shortNone: 'None',
        shortUnchecked: 'Pending',
        shortUnknown: 'Unknown',
        statusChanged: 'Changed',
        statusImportantChanged: 'Important change',
        statusNone: 'No change',
        statusNotApplicable: 'Not applicable',
        statusUnchecked: 'Unchecked',
        statusUnknown: 'Needs review / unknown',
        uncheckedCount: 'Unchecked {{count}}',
        unitHeader: 'Unit',
        unknownCount: 'Unknown {{count}}',
        workbenchDescription: 'Review every cell, then send only the changed cells to the DRBFM design change.',
        workbenchTitle: 'Function List × Part Tree Change-Point Review',
      },
      progress: {
        buildMatrix: 'Build matrix',
        countermeasures: 'Countermeasures',
        extractChanges: 'Change extraction',
        failureModes: 'Failure modes',
        normalize: 'Normalization',
        prefillMatrix: 'AI draft',
        promoteToDesignChange: 'Promote to design change',
        review: 'Review',
        reviewChanges: 'Change review',
      },
      stageRun: {
        empty: 'No runs yet.',
        title: 'Run history',
      },
      staged: {
        exportDraft: 'Export draft',
        exportReviewed: 'Export reviewed',
        exportScored: 'Export scored',
        generateCountermeasures: 'Generate countermeasures',
        proceedToFailureModes: 'Proceed to failure mode generation',
        readinessStatus: 'Content status: {{readiness}} / {{blockers}} unresolved',
        targetPartLabel: 'Target part',
      },
      workbenches: {
        countermeasureEmpty: 'No countermeasure candidates yet.',
        countermeasureReviewDescription: 'Review the target cause, mechanism, verification method, and acceptance criteria together.',
        countermeasureReviewTitle: 'Countermeasure Review',
        designChangeEmpty: 'No design change candidates yet.',
        designChangeReviewTitle: 'Design Change Review',
        failureModeEmpty: 'No failure mode candidates yet.',
        failureModeReviewDescription: 'AI severity is a candidate value; the RPN is not finalised until S/O/D are confirmed.',
        failureModeReviewTitle: 'Failure Mode Review',
        failureModeTargetCount: '{{count}} targeted for failure mode generation',
        statusAccepted: 'Accepted',
        statusAiCandidate: 'AI candidate',
        statusDeferred: 'Deferred',
        statusModified: 'Modified',
        statusNeedsInfo: 'Needs review',
        statusRejected: 'Rejected',
        statusSuperseded: 'Superseded',
      },
    };

export default workflow;
